import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  ScrollView,
  Pressable,
  Animated,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { LayoutChangeEvent } from 'react-native';

import activelyChargingIcon from '../../assets/activelyChargingIcon.png';
import connectedNotCharging from '../../assets/connectedNotCharging.png';
import notConnectedOrCharging from '../../assets/notConnectedOrCharging.png';
import scheduledChargeIcon from '../../assets/scheduleChargeIcon.png';

import {
  checkinCharger,
  getActiveChargeData,
} from '../services/charger-service';
import { notificationService } from '../services/notification-service';
import { updateSelectedCharger } from '../store/actions/charging-actions';
import { ActiveChargeStatus, ChargerAttributes } from '../models/charger-model';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getCurrentUtcSeconds,
  secondsToHHMMSS,
} from '../components/common/utils/date-time-conversions';
import { checkIsPeak } from '../components/common/utils/peak-off-peak-field';
import { AppDispatch } from '../store';
import { getActiveCharges } from '../store/actions/user-actions';
import { ActiveChargeAttributes } from '../models/active-charges-model';
import { isSuccessful } from '../services/api-requests';
import LoadingButton from '../components/LoadingButton';
import SlideToActivateButton from '../components/SlideToActivateButton';
import Scheduler from './Scheduler';
import { routeTitleMap } from '../components/common/utils/route-title-map';
import {
  ChargerSchedule,
  deleteSchedule,
  SendSchedule,
  setSchedule,
} from '../store/actions/scheduling-actions';
import { Modal } from 'react-native-paper';
import LoadingSpinnerOverlay from '../components/LoadingSpinnerOverlay';

const { width, height: windowHeight } = Dimensions.get('window');
const MIN_IMAGE_HEIGHT = 40;
const MAX_IMAGE_HEIGHT = 180;
const CAR_THRESHOLD = 180;
const ESTIMATED_CONTENT_HEIGHT_BELOW_IMAGE = 480;

const ChargingState = {
  CONNECTED_AND_CHARGING: 0,
  CONNECTED_AND_NOT_CHARGING: 1,
  DISCONNECTED: 3,
  SCHEDULED_CONNECTED: 4,
  SCHEDULED_NOT_CONNECTED: 5,
  UNKNOWN: -1,
};

const TAB_BAR_HEIGHT = 150; // Adjust as needed
const SAFE_AREA_PADDING = 80; // Adjust as needed

export default function ChargingScreen(props: { navigation: any; route: any }) {
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  const [localTimer, setLocalTimer] = useState<number>(0);
  const [kWhCharged, setKWhCharged] = useState<number>(0);
  const [kwhDisplayValue, setKwhDisplayValue] = useState(kWhCharged);
  const [estPrice, setEstPrice] = useState<number>(0);
  const [chargingStatus, setChargingStatus] = useState(ChargingState.UNKNOWN);
  const [activeChargeStatus, setActiveChargeStatus] =
    useState<ActiveChargeStatus>();

  const [scheduleStartTime, setScheduleStartTime] = useState<Date>(
    props.route.params.scheduleObj
      ? new Date(props.route.params.scheduleObj.startTime)
      : new Date()
  );
  const [scheduleEndTime, setScheduleEndTime] = useState<Date>(
    props.route.params.scheduleObj
      ? new Date(props.route.params.scheduleObj.endTime)
      : new Date()
  );
  const [selectedMaxKwh, setSelectedMaxKwh] = useState<number | null>(
    props.route.params.scheduleObj?.max_kwh ?? null
  );
  const [scheduleSet, setScheduleSet] = useState<boolean>(
    props.route.params.scheduleObj ? true : false
  );
  const [scheduleObj, setScheduleObj] = useState<ChargerSchedule | null>(
    props.route.params.scheduleObj
  );
  const dispatch: AppDispatch = useDispatch();

  const pollTimer = useRef<NodeJS.Timeout | null>(null);
  const lastServerUpdate = useRef<number>(0); // Track last server update time
  let timer: NodeJS.Timeout | undefined;

  const selectedCharger: ChargerAttributes = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  ) as ChargerAttributes;

  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const suppressChargeSummaryNavigation: boolean = useSelector(
    (state: RootState) => state.user.suppressChargeSummaryNavigation
  );
  const [localActiveCharge, setLocalActiveCharge] =
    useState<ActiveChargeAttributes | null>(null);

  const startTime = useRef<Date>(new Date()); //useRef to persist value across renders
  const colorAnimation = useRef(new Animated.Value(0)).current;
  const pulseRef = useRef<Animated.CompositeAnimation | null>(null);
  const fadeAnimation = useRef(new Animated.Value(1)).current; // Animation for fade effect
  const [showCancelLoader, setShowCancelLoader] = useState<boolean>(false);
  const [belowImageHeight, setBelowImageHeight] = useState(0);
  const [isWaitingForCharge, setIsWaitingForCharge] = useState<boolean>(false);
  const [hasCheckedForCharge, setHasCheckedForCharge] =
    useState<boolean>(false);
  const [dynamicImageHeight, setDynamicImageHeight] = useState<number>(
    Math.max(
      MIN_IMAGE_HEIGHT,
      Math.min(
        MAX_IMAGE_HEIGHT,
        windowHeight - ESTIMATED_CONTENT_HEIGHT_BELOW_IMAGE
      )
    )
  );

  const handleBelowImageLayout = (event: LayoutChangeEvent) => {
    const measuredHeight = event.nativeEvent.layout.height;
    setBelowImageHeight(measuredHeight);
    
    // Calculate if car should be shown based on current image height
    const wouldShowCar = dynamicImageHeight > CAR_THRESHOLD;
    
    // Calculate available space for image, accounting for car, location block, and safe area
    const carAndLocationHeight = wouldShowCar ? carHeight + locationBlockHeight + 56 : locationBlockHeight + 16;
    const availableSpace = windowHeight - measuredHeight - carAndLocationHeight - TAB_BAR_HEIGHT - SAFE_AREA_PADDING;
    
    // Update image height to ensure content fits
    const newImageHeight = Math.max(
      MIN_IMAGE_HEIGHT,
      Math.min(MAX_IMAGE_HEIGHT, availableSpace)
    );
    
    if (newImageHeight !== dynamicImageHeight) {
      setDynamicImageHeight(newImageHeight);
    }
  };

  const imageHeight = dynamicImageHeight;
  const showCar = imageHeight > CAR_THRESHOLD;
  const carHeight = 74;
  const locationBlockHeight = 40;
  const showCarAndLocation = imageHeight > carHeight + locationBlockHeight + 40;

  useEffect(() => {
    if (!scheduleStartTime) return;

    const start = new Date(scheduleStartTime).getTime();
    const now = Date.now();
    const delay = start - now;

    if (delay <= 0) {
      // If the scheduled time has already passed, update immediately.
      setScheduleSet(false);
      return;
    }

    const timerId = setTimeout(() => {
      setScheduleSet(false);
    }, delay);

    // Clean up the timer if scheduleStartTime changes or the component unmounts.
    return () => clearTimeout(timerId);
  }, [scheduleStartTime]);

  const setScheduleTimes = async (
    startTime: Date,
    endTime?: Date | null,
    maxKwh?: number | null
  ) => {
    setScheduleStartTime(startTime);
    if (endTime) {
      setScheduleEndTime(endTime);
    } else {
      setScheduleEndTime(
        new Date(scheduleStartTime.getTime() + 12 * 60 * 60 * 1000)
      );
    }
    setSelectedMaxKwh(maxKwh ?? null);
    if (startTime < new Date()) {
      setScheduleSet(true);
    } else {
      setScheduleSet(false);
    }

    //reset the schedule within the same order
    if (selectedCharger && selectedCharger.uuid && localActiveCharge) {
      const sendSchedule: SendSchedule = {
        start_time: startTime.toISOString(),
        end_time:
          endTime?.toISOString() ??
          new Date(startTime.getTime() + 12 * 60 * 60 * 1000).toISOString(),
        chargerId: selectedCharger.uuid,
        orderId: props.route.params.order.id,
        max_kwh: maxKwh,
      };
      const scheduleResp = await setSchedule(sendSchedule);
      let scheduleData;
      if (Array.isArray(scheduleResp)) {
        scheduleData = scheduleResp[0];
      } else {
        scheduleData = scheduleResp;
      }
      if (scheduleData) setScheduleObj(scheduleData);
    } else {
      notificationService.sendNotification(`error`, 'Error changing schedule');
    }
  };

  const clearSchedule = async () => {
    const d = new Date();
    const ed = new Date(d.getTime() + 12 * 60 * 60 * 1000);
    setScheduleStartTime(d);
    setScheduleEndTime(ed);

    //remove the set schedule and begin charging immediately
    //await deleteSchedule (scheduleObj.scheduleId);
    await setScheduleTimes(d, ed, selectedMaxKwh);
  };

  useEffect(() => {
    if (chargingStatus === ChargingState.CONNECTED_AND_CHARGING) {
      // Start the pulsing animation
      pulseRef.current = Animated.loop(
        Animated.sequence([
          Animated.timing(colorAnimation, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false,
          }),
          Animated.timing(colorAnimation, {
            toValue: 0,
            duration: 1000,
            useNativeDriver: false,
          }),
        ])
      );
      pulseRef.current.start();
    } else {
      // Stop the pulsing animation
      if (pulseRef.current) {
        pulseRef.current.stop();
        colorAnimation.setValue(0); // Reset color to initial state
      }
    }

    // Cleanup on unmount
    return () => {
      if (pulseRef.current) {
        pulseRef.current.stop();
      }
    };
  }, [colorAnimation, chargingStatus]);

  const interpolatedColor = colorAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['#FFFFFF', '#39BEB7'],
  });

  useEffect(() => {
    // Start fade-out animation
    Animated.timing(fadeAnimation, {
      toValue: 0, // Fade out to 0
      duration: 500, // Duration of fade-out
      useNativeDriver: true,
    }).start(() => {
      // After fade-out completes, update the display value and fade back in
      setKwhDisplayValue(kWhCharged);
      Animated.timing(fadeAnimation, {
        toValue: 1, // Fade in to 1
        duration: 500, // Duration of fade-in
        useNativeDriver: true,
      }).start();
    });
  }, [kWhCharged]); // Run this effect when kWhCharged changes

  const pollChargingStatus = async (orderId: string) => {
    return await getActiveChargeData(orderId);
  };

  const handleChargingDone = async (orderId: string) => {
    try {
      const response = await checkinCharger(orderId);
      setLocalActiveCharge(null);
      if (isSuccessful(response.status)) {
        notificationService.sendNotification(
          'success',
          'Charger successfully checked in!'
        );
        updateSelectedChargerAndNavigateToSummary(orderId);
      } else {
        notificationService.sendNotification(
          'error',
          'Error checking in your charger!'
        );
      }
    } catch (e) {
      notificationService.sendNotification(
        'error',
        'Error checking in your charger!'
      );
      //TODO: need to figure out if charger is still attached and charging here,
      // ideally we should just try to shut down the charger from here and keep going
      console.error(e);
    } finally {
      setShowCancelLoader(false);
    }

    if (pollTimer.current) {
      clearInterval(pollTimer.current);
    }

    if (timer) {
      clearInterval(timer);
    }
  };

  const updateSelectedChargerAndNavigateToSummary = (orderId: string) => {
    // Clear polling interval BEFORE navigation to prevent continued polling
    if (pollTimer.current) {
      console.log('[ChargingScreen] Clearing polling interval before navigation');
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }

    if (localActiveCharge?.charger.uuid) {
      dispatch(updateSelectedCharger(localActiveCharge.charger.uuid));
    }

    // Check if navigation should be suppressed (e.g., when user disabled charger from edit page)
    if (suppressChargeSummaryNavigation) {
      console.log('Suppressing automatic navigation to ChargeSummary - user disabled charger from edit page');
      // Still clean up local state but don't navigate
      setLocalActiveCharge(null);
      // Navigate back to previous screen instead
      props.navigation.goBack();
      return;
    }

    //TODO: should be a summary screen of the total charge
    const endTime = new Date();

    props.navigation.navigate('ChargeSummary', {
      charger: localActiveCharge!.charger,
      startTime: startTime.current.toISOString(),
      endTime: endTime.toISOString(),
      orderId: orderId,
      fromScreen: 'ChargingScreen',
    });
  };

  // Poll function for charger status
  let now = new Date();
  const pollAndUpdate = async () => {
    let activeChargeResponse;
    if (localActiveCharge) {
      activeChargeResponse = await pollChargingStatus(
        localActiveCharge.order.id
      );
      if (isSuccessful(activeChargeResponse.status)) {
        setActiveChargeStatus(activeChargeResponse.payload!);
        // Check if the message indicates completion
        if (
          activeChargeResponse?.payload?.message &&
          activeChargeResponse.payload?.message === 'complete'
        ) {
          updateSelectedChargerAndNavigateToSummary(localActiveCharge.order.id); // Call handleChargingDone with the order id
          return; // Exit the function to stop further checks if charging is done
        }
        if (activeChargeResponse.payload?.chargerCarData) {
          let isVehicleCharging =
            activeChargeResponse.payload?.chargerCarData?.vehicle_charging;
          let isVehicleConnected =
            activeChargeResponse.payload?.chargerCarData?.vehicle_connected;

          //check the scheduled payload. Are we past the scheduled start time?

          let isScheduled = false;
          const startTimeUTC = activeChargeResponse.payload.schedule?.startTime;

          if (startTimeUTC) {
            // Convert the UTC start time to a Date object (the Date object automatically converts to local time)
            const scheduleStartDate = new Date(startTimeUTC);
            now = new Date();

            // If the scheduled start time is in the future, then charging is scheduled to start
            if (scheduleStartDate > now) {
              isScheduled = true;
            }
          }
          if (isScheduled) {
            if (isVehicleConnected) {
              setChargingStatus(ChargingState.SCHEDULED_CONNECTED);
            } else {
              setChargingStatus(ChargingState.SCHEDULED_NOT_CONNECTED);
            }
          } else {
            if (isVehicleCharging && isVehicleConnected) {
              setChargingStatus(ChargingState.CONNECTED_AND_CHARGING);
            } else if (isVehicleConnected === true) {
              setChargingStatus(ChargingState.CONNECTED_AND_NOT_CHARGING);
            } else {
              setChargingStatus(ChargingState.DISCONNECTED);
            }
          }
        } else {
          setChargingStatus(ChargingState.UNKNOWN);
        }
      } else {
        setChargingStatus(ChargingState.UNKNOWN);
      }
    } else {
      setChargingStatus(ChargingState.UNKNOWN);
    }
  };

  useEffect(() => {
    // fetch the active charges
    dispatch(getActiveCharges());
  }, []);

  useEffect(() => {
    if (activeCharges && activeCharges.length > 0) {
      //console.log("orderId is:", props.route.params.order.id);
      const activeCharge = activeCharges?.find(
        (charge) => charge.order.id === props.route.params.order.id
      );
      // console.log('got active charge:', activeCharge);
      if (activeCharge) {
        //console.log("setting active charge:", activeCharge);
        setLocalActiveCharge(activeCharge);
        setIsWaitingForCharge(false);
        setHasCheckedForCharge(true);
      } else if (!hasCheckedForCharge) {
        // Only show error if we've already waited and checked
        notificationService.sendNotification(
          'error',
          'Unable to locate your charge'
        );
        setHasCheckedForCharge(true);
      }

      if (selectedCharger?.can_schedule && activeCharge?.schedule) {
        //set the schedule feature
        const startTime = new Date(activeCharge.schedule.startTime);
        const endTime = new Date(activeCharge.schedule.endTime);
        setScheduleStartTime(startTime);
        setScheduleEndTime(endTime);
        setSelectedMaxKwh(activeCharge.schedule.max_kwh ?? null);
        setScheduleSet(true);
      }
    } else if (!hasCheckedForCharge) {
      setLocalActiveCharge(null);
    }
  }, [activeCharges, hasCheckedForCharge]);

  // Add a delay before allowing charge checking to account for server lag
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsWaitingForCharge(false);
      setHasCheckedForCharge(true);
      // Re-fetch active charges after the delay
      dispatch(getActiveCharges());
    }, 8000); // 8 second delay

    return () => clearTimeout(timer);
  }, []);

  // Set initial waiting state
  useEffect(() => {
    setIsWaitingForCharge(true);
  }, []);

  useEffect(() => {
    const startPolling = () => {
      pollAndUpdate();
      pollTimer.current = setInterval(pollAndUpdate, 5000); //set the polling interval
      // pollTimer = setInterval(async () => {
      //     pollAndUpdate()
      // }, 5000)
    };

    const stopPolling = () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current); // Clear the interval
        pollTimer.current = null;
      }
    };

    startPolling(); //only run on component mount

    return () => stopPolling(); //cleanup when component unmounts
  }, [localActiveCharge]);

  // Handle polling based on focus state
  useFocusEffect(
    useCallback(() => {
      return () => {
        if (pollTimer.current) {
          clearInterval(pollTimer.current); // Clear interval when screen loses focus
        }
        //update the application state if the charge is active
        dispatch(getActiveCharges());
      };
    }, [])
  );

  useEffect(() => {
    //the activeChargeStatus contains all of the connected and kwh data for the current charge
    if (
      activeChargeStatus &&
      localActiveCharge &&
      activeChargeStatus.usageData
    ) {
      const currentTime = Date.now();
      const timeSinceLastUpdate = currentTime - lastServerUpdate.current;
      const oneMinute = 60 * 1000; // 60 seconds in milliseconds

      // Only update from server data once per minute to match 3rd party service resolution
      if (timeSinceLastUpdate >= oneMinute) {
        console.log('Updating from server data (once per minute)');
        setEstPrice(
          activeChargeStatus.peakPrice + activeChargeStatus.offPeakPrice
        );
        const serverTime =
          activeChargeStatus.usageData.peakTimeSeconds +
          activeChargeStatus.usageData.offPeakTimeSeconds;
        if (localTimer < serverTime) {
          setKWhCharged(
            activeChargeStatus.usageData.peakKwh +
              activeChargeStatus.usageData.offPeakKwh
          );
          setTimeElapsed(serverTime);
          setLocalTimer(serverTime);
        }
        lastServerUpdate.current = currentTime;
      } else {
        console.log(
          `Skipping server update, last update was ${Math.round(
            timeSinceLastUpdate / 1000
          )}s ago`
        );
      }
    }
  }, [activeChargeStatus]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const d = new Date();
    // Increment local timer every second
    if (chargingStatus === ChargingState.CONNECTED_AND_CHARGING) {
      interval = setInterval(() => {
        setLocalTimer((prevTimer) => prevTimer + 1);
        setKWhCharged(
          (prevKWh) =>
            prevKWh +
            ((selectedCharger?.charging_capacity ?? 40) * 220) / 3600 / 1000
        ); //charging
        setEstPrice(
          (prevPrice) =>
            prevPrice +
            (checkIsPeak(d, selectedCharger?.peak_selection ?? '')
              ? activeChargeStatus?.peakPrice ??
                selectedCharger?.financials?.price_kwh_peak ?? 0
              : activeChargeStatus?.offPeakPrice ??
                selectedCharger?.financials?.price_kwh_off_peak ?? 0) *
              (kWhCharged / 1000)
        );
      }, 1000);
    }

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [chargingStatus]);

  let status = 'Connected and charging';
  let image;
  let tintColor;

  switch (chargingStatus) {
    case ChargingState.CONNECTED_AND_CHARGING:
      status = 'Connected and charging';
      image = activelyChargingIcon;
      tintColor = undefined;
      break;
    case ChargingState.CONNECTED_AND_NOT_CHARGING:
      status = 'Connected, not charging';
      image = connectedNotCharging;
      tintColor = undefined;
      break;
    case ChargingState.DISCONNECTED:
      status = 'Not connected to charger';
      image = notConnectedOrCharging;
      tintColor = undefined;
      break;
    case ChargingState.SCHEDULED_CONNECTED:
      status = 'Scheduled and connected';
      image = scheduledChargeIcon;
      tintColor = '#FBD644';
      break;
    case ChargingState.SCHEDULED_NOT_CONNECTED:
      status = 'Scheduled, not connected';
      image = scheduledChargeIcon;
      tintColor = '#F97777';
      break;
    default:
      status = 'Retrieving status...';
      break;
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={[styles.containerBgColor, { flex: 1 }]}>
        <Image
          source={require('../../assets/ActiveChargingBackground.jpg')}
          style={{
            width: '100%',
            height: imageHeight,
            marginBottom: 16,
          }}
          alt="Background Image"
        />
        {showCar && (
          <View style={{ alignItems: 'center', width: '100%' }}>
            <Image
              source={require('../../assets/CarStatic.png')}
              style={{ width: 227, height: 74, alignSelf: 'center' }}
              alt="car"
            />
          </View>
        )}
        {/* Location block always rendered */}
        <View
          style={Platform.select({
            web: {
              alignSelf: 'center',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              marginTop: 16,
            },
            default: {
              alignSelf: 'center',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              marginTop: 16,
            },
          })}
        >
          <Image
            source={require('../../assets/ChargeLocation.png')}
            style={{ width: 40, height: 40 }}
            alt="Location"
          />
          <View style={{ flexShrink: 1 }}>
            <Text className="text-base font-bold text-white">{`${
              localActiveCharge?.charger.name || 'Loading...'
            }`}</Text>
            <Text className="text-sm font-semibold text-white">{`${
              localActiveCharge?.charger.full_address || ''
            }`}</Text>
          </View>
        </View>

        {/* Show loading indicator when waiting for charge data */}
        {isWaitingForCharge && !localActiveCharge && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#39BEB7" />
            <Text style={styles.loadingText}>Loading your charge data...</Text>
          </View>
        )}

        <View onLayout={handleBelowImageLayout}>
          <View className="relative">
            <View className="relative mt-4">
              <Image
                source={require('../../assets/chargingHistoryBgImage.png')}
                style={{
                  height:
                    Platform.OS === 'web'
                      ? width <= 430
                        ? width * 0.64
                        : 270
                      : 300,
                  width: '100%',
                }}
                resizeMode="cover"
              />
              <View
                className="flex absolute flex-row items-center w-full"
                style={{
                  height: width <= 430 ? width * 0.35 : 150,
                }}
              >
                <View className="flex-1 items-center w-50">
                  <View className="relative">
                    <Image
                      source={image}
                      style={{
                        height: 75,
                        width: 75,
                        tintColor: tintColor,
                        marginTop: 15,
                      }}
                    />
                  </View>
                  <Text
                    className="text-xs font-normal text-white"
                    style={{
                      //fontSize: 10,
                      textAlign: 'center',
                      flexWrap: 'wrap',
                      maxWidth: 120,
                      marginTop: 0,
                    }}
                    numberOfLines={2}
                  >
                    {status}
                  </Text>
                </View>
                <View className="flex-1 items-center w-50">
                  <View className="relative">
                    <Image
                      source={require('../../assets/timeElapsedIcon.png')}
                      style={{
                        height: 80,
                        width: 80,
                      }}
                    />
                    <Text
                      style={Platform.select({
                        web: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        },
                        default: {
                          position: 'absolute',
                          top: '40%',
                          left: '5%',
                        },
                      })}
                      className="absolute text-sm font-bold text-white"
                    >
                      {!scheduleSet && secondsToHHMMSS(localTimer)}
                    </Text>
                  </View>
                  <Text className="text-xs font-normal text-white">
                    {!scheduleSet && `Time elapsed`}
                  </Text>
                </View>
              </View>
              <View
                className="flex absolute flex-row items-center w-full"
                style={{
                  height: width <= 430 ? width * 0.33 : 140,
                  marginTop: width <= 430 ? width * 0.33 : 140,
                }}
              >
                <View
                  className={
                    Platform.OS === 'web'
                      ? 'items-center flex-1 w-50'
                      : 'items-center flex-1 w-50 mt-8'
                  }
                >
                  <View className="flex flex-row gap-3 items-center">
                    <Image
                      source={require('../../assets/totalKwhCharged.png')}
                      style={{
                        height: 60,
                        width: 60,
                      }}
                    />
                    <View>
                      <Animated.Text
                        style={{
                          color: interpolatedColor,
                        }}
                        className="text-2xl font-bold"
                      >
                        {kwhDisplayValue.toFixed(3)}
                      </Animated.Text>
                      <Text className="text-xs font-medium text-white">
                        kWh
                      </Text>
                    </View>
                  </View>
                  <Text className="mt-3 text-xs font-normal text-white">
                    Total charged
                  </Text>
                </View>
                <View
                  className={
                    Platform.OS === 'web'
                      ? 'items-center flex-1 w-50'
                      : 'items-center flex-1 w-50 mt-8'
                  }
                >
                  <View className="relative">
                    <Image
                      source={require('../../assets/AccruedCostBackground.png')}
                      style={{
                        height: 75,
                        width: 110,
                      }}
                    />

                    <Animated.Text
                      style={Platform.select({
                        web: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translateX(-50%) translateY(-50%)',
                          fontSize: estPrice >= 10 ? 20 : 24, // Smaller font for 4+ digit values ($10.00+)
                          fontWeight: "500",
                          color: interpolatedColor,
                        },
                        default: {
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: [{ translateX: -75 }, { translateY: -12 }], // Center the text (adjusted for proper centering)
                          fontSize: estPrice >= 10 ? 20 : 24, // Smaller font for 4+ digit values ($10.00+)
                          fontWeight: "500",
                          color: interpolatedColor,
                        },
                      })}
                    >
                      {`$${estPrice.toFixed(2)}`}
                    </Animated.Text>
                  </View>
                  <Text className="text-xs font-normal text-white">
                    Est. cost
                  </Text>
                </View>
              </View>
            </View>
            {scheduleSet && (
              <View>
                {/* Instructional Text on Top */}
                <Text
                  style={{
                    fontSize: 12,
                    color: 'white',
                    textAlign: 'center',
                    marginVertical: 4,
                    marginTop: 10,
                  }}
                >
                  Tap below to adjust your schedule or to cancel the scheduled
                  charge.
                </Text>

                {/* Row containing Scheduler and Cancel button */}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 10,
                    gap: 12,
                  }}
                >
                  {/* Scheduler takes available space */}
                  <View style={{ flex: 1, maxWidth: 200 }}>
                    <Scheduler
                      selectedCharger={selectedCharger}
                      scheduleDate={scheduleStartTime}
                      setScheduleTimes={setScheduleTimes}
                      clearSchedule={clearSchedule}
                      presetDate={scheduleStartTime}
                    />
                  </View>
                  {/* Cancel button on the right */}
                  <Pressable
                    onPress={() => {
                      setShowCancelLoader(true);
                      handleChargingDone(props.route.params.order.id);
                    }}
                    style={{
                      paddingVertical: 16,
                      paddingHorizontal: 8,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        color: 'white',
                        textDecorationLine: 'underline',
                      }}
                    >
                      Cancel
                    </Text>
                  </Pressable>
                </View>
              </View>
            )}
            {!scheduleSet && (
              <View>
                <View>
                  <Text
                    className="mt-4 font-medium text-center text-white"
                    style={{ fontSize: 10 }}
                  >
                    To stop charging, unplug the car at any time or slide below.
                  </Text>
                </View>
                <SlideToActivateButton
                  onComplete={async () => {
                    // Simulate a network request
                    await handleChargingDone(props.route.params.order.id);
                  }}
                  backgroundColor={styles.BgColor.backgroundColor} // Optional custom color
                >
                  Stop Charging
                </SlideToActivateButton>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* On clicking cancel button */}
      <LoadingSpinnerOverlay visible={showCancelLoader} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  BgColor: {
    backgroundColor: '#F75555',
  },
  BgColor1: {
    backgroundColor: '#39BEB780',
  },
  BgColor2: {
    backgroundColor: '#F97777',
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  TextColor1: {
    color: '#E8E8E9',
  },
  textColor2: {
    color: '#A3A5A6',
  },
  textColor3: {
    color: '#8D8E90',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#39BEB7',
    textAlign: 'center',
  },
});
